import { Autocomplete, Circle, GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { LocateFixed, MapPin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import type { CountryCode } from '@/constants/countries';
import {
  defaultMapCenter,
  defaultMapZoom,
  GOOGLE_MAP_LIBRARIES,
  googleMapsApiKey,
  selectedLocationZoom,
} from '@/lib/google-maps-config';
import { cn } from '@/utils/cn';

export interface ChurchLocation {
  latitude: number;
  longitude: number;
  address?: string | null;
}

export interface LocationPickerProps {
  value: ChurchLocation | null;
  onChange: (value: ChurchLocation | null) => void;
  country?: CountryCode | null;
  /** When set with a pinned location, draws the GPS check-in area on the map. */
  radiusMetres?: number | null;
  className?: string;
  mapClassName?: string;
}

const COUNTRY_CENTERS: Partial<Record<CountryCode, google.maps.LatLngLiteral>> = {
  GH: { lat: 5.6037, lng: -0.187 },
  NG: { lat: 6.5244, lng: 3.3792 },
  US: { lat: 39.8283, lng: -98.5795 },
  GB: { lat: 51.5074, lng: -0.1278 },
  CA: { lat: 43.6532, lng: -79.3832 },
  ZA: { lat: -26.2041, lng: 28.0473 },
  KE: { lat: -1.2921, lng: 36.8219 },
};

const mapContainerStyle = { width: '100%', height: '100%' };

const formatCoordinate = (value: number): string => value.toFixed(5);

const placeSearchLabel = (place: google.maps.places.PlaceResult): string =>
  place.formatted_address ?? place.name ?? '';

const geocodeSearchLabel = (result: google.maps.GeocoderResult): string =>
  result.formatted_address ?? '';

const readBrandPrimaryColor = (): string =>
  getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim() ||
  '#0B1E3A';

export const LocationPicker = ({
  value,
  onChange,
  country,
  radiusMetres,
  className,
  mapClassName,
}: LocationPickerProps) => {
  const { t } = useTranslation('churches');
  const apiKey = googleMapsApiKey();

  const { isLoaded, loadError } = useLoadScript({
    id: 'trinity-google-maps',
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const placesLoaded = isLoaded && Boolean(globalThis.google?.maps?.places);

  const fallbackCenter = useMemo(
    () => (country ? (COUNTRY_CENTERS[country] ?? defaultMapCenter) : defaultMapCenter),
    [country],
  );

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [searchDisplay, setSearchDisplay] = useState('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const skipValueSync = useRef(false);
  const isEditingSearchRef = useRef(false);

  const setInputValue = useCallback((text: string) => {
    if (inputRef.current) {
      inputRef.current.value = text;
    }
    setSearchDisplay(text);
  }, []);

  const markerPosition = useMemo(
    () =>
      value
        ? { lat: value.latitude, lng: value.longitude }
        : null,
    [value?.latitude, value?.longitude],
  );

  const checkInRadiusMetres =
    markerPosition && radiusMetres != null && radiusMetres > 0 ? radiusMetres : null;

  const checkInAreaColors = useMemo(() => {
    const color = readBrandPrimaryColor();
    return {
      fillColor: color,
      strokeColor: color,
      fillOpacity: 0.14,
      strokeOpacity: 0.7,
      strokeWeight: 2,
    };
  }, [checkInRadiusMetres]);

  const applyLocation = useCallback(
    (position: google.maps.LatLngLiteral, label?: string) => {
      isEditingSearchRef.current = false;
      skipValueSync.current = true;
      onChange({
        latitude: position.lat,
        longitude: position.lng,
        address: label?.trim() || null,
      });
      if (label) setInputValue(label);
      map?.panTo(position);
      map?.setZoom(selectedLocationZoom);
    },
    [map, onChange, setInputValue],
  );

  const handleSearchInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      isEditingSearchRef.current = true;
      const next = event.target.value;
      setSearchDisplay(next);

      const savedAddress = value?.address?.trim() ?? '';
      if (value !== null && next !== savedAddress) {
        skipValueSync.current = true;
        onChange(null);
      }
    },
    [onChange, value],
  );

  useEffect(() => {
    if (skipValueSync.current) {
      skipValueSync.current = false;
      return;
    }

    if (isEditingSearchRef.current) {
      return;
    }

    if (!value) {
      setInputValue('');
      return;
    }

    const savedAddress = value.address?.trim();
    if (savedAddress) {
      setInputValue(savedAddress);
    }
  }, [setInputValue, value]);

  useEffect(() => {
    if (!map || !value || skipValueSync.current) {
      skipValueSync.current = false;
      return;
    }

    const position = { lat: value.latitude, lng: value.longitude };
    map.panTo(position);
    map.setZoom(selectedLocationZoom);

    const savedAddress = value.address?.trim();
    if (savedAddress || isEditingSearchRef.current) {
      return;
    }

    if (globalThis.google?.maps?.Geocoder) {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const label = geocodeSearchLabel(results[0]);
          if (!isEditingSearchRef.current && label) {
            setInputValue(label);
            skipValueSync.current = true;
            onChange({
              latitude: value.latitude,
              longitude: value.longitude,
              address: label,
            });
          }
        }
      });
    }
  }, [map, onChange, setInputValue, value]);

  useEffect(() => {
    if (!map || !markerPosition || !checkInRadiusMetres || !globalThis.google?.maps?.Circle) {
      return;
    }

    const circle = new globalThis.google.maps.Circle({
      center: markerPosition,
      radius: checkInRadiusMetres,
    });
    const bounds = circle.getBounds();
    if (bounds) {
      map.fitBounds(bounds, 48);
    }
  }, [checkInRadiusMetres, map, markerPosition]);

  const onMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      setLocationError(null);

      if (globalThis.google?.maps?.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: position }, (results, status) => {
          const label =
            status === 'OK' && results?.[0] ? geocodeSearchLabel(results[0]) : undefined;
          applyLocation(position, label);
        });
        return;
      }

      applyLocation(position);
    },
    [applyLocation],
  );

  const onPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry?.location) return;

    setLocationError(null);
    const position = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };
    applyLocation(position, placeSearchLabel(place));
  }, [applyLocation]);

  const getCurrentLocation = useCallback(() => {
    setLocationError(null);

    if (
      !globalThis.location?.hostname.includes('localhost') &&
      !globalThis.isSecureContext
    ) {
      setLocationError(t('settings.locationGeolocationInsecure'));
      return;
    }

    if (!navigator.geolocation) {
      setLocationError(t('settings.locationGeolocationUnsupported'));
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        if (globalThis.google?.maps?.Geocoder) {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: coords }, (results, status) => {
            const label =
              status === 'OK' && results?.[0] ? geocodeSearchLabel(results[0]) : undefined;
            applyLocation(coords, label);
            setIsLoadingLocation(false);
          });
          return;
        }

        applyLocation(coords);
        setIsLoadingLocation(false);
      },
      (error) => {
        const message =
          error.code === error.PERMISSION_DENIED
            ? t('settings.locationGeolocationDenied')
            : error.code === error.POSITION_UNAVAILABLE
              ? t('settings.locationGeolocationUnavailable')
              : error.code === error.TIMEOUT
                ? t('settings.locationGeolocationTimeout')
                : t('settings.locationGeolocationUnavailable');
        setLocationError(message);
        setIsLoadingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }, [applyLocation, t]);

  const onMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!event.latLng) return;
      const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      setLocationError(null);

      if (globalThis.google?.maps?.Geocoder) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: position }, (results, status) => {
          const label =
            status === 'OK' && results?.[0] ? geocodeSearchLabel(results[0]) : undefined;
          applyLocation(position, label);
        });
        return;
      }

      applyLocation(position);
    },
    [applyLocation],
  );

  const clearLocation = useCallback(() => {
    isEditingSearchRef.current = false;
    setInputValue('');
    setLocationError(null);
    onChange(null);
    map?.panTo(fallbackCenter);
    map?.setZoom(defaultMapZoom);
  }, [fallbackCenter, map, onChange, setInputValue]);

  if (!apiKey) {
    return (
      <p className={cn('text-body-sm text-fg-secondary', className)}>
        {t('settings.locationMapsUnavailable')}
      </p>
    );
  }

  if (loadError) {
    return (
      <p className={cn('text-body-sm text-danger', className)}>
        {t('settings.locationLoadError')}
      </p>
    );
  }

  if (!placesLoaded) {
    return (
      <div
        className={cn(
          'flex h-64 items-center justify-center rounded-lg border border-border bg-surface-sunken',
          mapClassName,
          className,
        )}
      >
        <Spinner />
      </div>
    );
  }

  const autocompleteRestrictions = country
    ? { country: country.toLowerCase() }
    : undefined;

  return (
    <div className={cn('space-y-3', className)}>
      <Autocomplete
        onLoad={(instance) => {
          autocompleteRef.current = instance;
        }}
        onPlaceChanged={onPlaceChanged}
        restrictions={autocompleteRestrictions}
      >
        <Input
          ref={inputRef}
          defaultValue=""
          onFocus={() => {
            isEditingSearchRef.current = true;
          }}
          onChange={handleSearchInput}
          placeholder={t('settings.locationSearch')}
          prefix={<MapPin className="h-4 w-4" aria-hidden />}
          autoComplete="off"
        />
      </Autocomplete>

      {locationError ? (
        <p className="text-body-sm text-danger" role="alert">
          {locationError}
        </p>
      ) : null}

      <div
        className={cn(
          'relative overflow-hidden rounded-lg border border-border',
          mapClassName ?? 'h-64',
        )}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={markerPosition ?? fallbackCenter}
          zoom={markerPosition ? selectedLocationZoom : defaultMapZoom}
          onLoad={setMap}
          onUnmount={() => setMap(null)}
          onClick={onMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
          }}
        >
          {markerPosition ? (
            <Marker
              position={markerPosition}
              draggable
              onDragEnd={onMarkerDragEnd}
            />
          ) : null}
          {checkInRadiusMetres ? (
            <Circle
              center={markerPosition!}
              radius={checkInRadiusMetres}
              options={checkInAreaColors}
            />
          ) : null}
        </GoogleMap>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="absolute right-3 bottom-3 shadow-e2"
          onClick={getCurrentLocation}
          loading={isLoadingLocation}
          disabled={isLoadingLocation}
        >
          {!isLoadingLocation ? <LocateFixed className="h-4 w-4" aria-hidden /> : null}
          {isLoadingLocation
            ? t('settings.locationGettingLocation')
            : t('settings.locationMyLocation')}
        </Button>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-body-sm text-fg-secondary">
          {value
            ? searchDisplay ||
              value.address?.trim() ||
              t('settings.locationCoordinates', {
                latitude: formatCoordinate(value.latitude),
                longitude: formatCoordinate(value.longitude),
              })
            : searchDisplay || t('settings.locationHint')}
        </p>
        {value ? (
          <Button type="button" variant="ghost" size="sm" onClick={clearLocation}>
            {t('settings.locationClear')}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
